import React from 'react';
import { getQuest } from '../../../../shared/content/generated/quests.gen';
import type { QuestCompletionNotice } from '../../contexts/InteractionContext';
import { questCardView } from '../../npc/hydrateNpcDialogue';
import { abilityIconPath, hasAbilityIcon } from '../../../rendering/abilities/AbilityIconRegistry';
import { ItemIcon } from '../../components/ItemIcon';
import { NpcItemChip, NpcRichText } from './NpcRichText';

/** What the turn-in just gave: XP, gold, each item with its icon, the ability taught. */
const ReceivedLine: React.FC<{ questId: string; playerClass?: string; alreadyKnown?: boolean }> = ({ questId, playerClass, alreadyKnown }) => {
  const rewards = questCardView(questId, undefined, playerClass);
  if (!rewards) return null;
  const ability = rewards.rewardAbility;
  return <p className="npc-completion-received" data-testid="npc-completion-received">
    <span className="npc-completion-received-label">Received</span>
    {rewards.rewardXP > 0 && <span className="npc-reward-chip">{rewards.rewardXP.toLocaleString()} XP</span>}
    {rewards.rewardGold > 0 && <span className="npc-reward-chip npc-text-item">
      <span className="npc-text-item-icon" aria-hidden="true"><ItemIcon itemId="gold" size={18} /></span>
      {rewards.rewardGold.toLocaleString()} Gold
    </span>}
    {(rewards.rewardItemDetails ?? []).map(item => <NpcItemChip key={item.id} itemId={item.id} quantity={item.quantity} />)}
    {ability && (
      alreadyKnown ? (
        <span className="npc-text-item npc-reward-ability-known" data-reward-ability-id={ability.id}>
          {hasAbilityIcon(ability.id) && <img className="npc-text-item-icon" src={abilityIconPath(ability.id)} alt="" width={20} height={20} />}
          Already known: {ability.name}
        </span>
      ) : (
        <span className="npc-text-item npc-reward-ability" data-reward-ability-id={ability.id}>
          {hasAbilityIcon(ability.id) && <img className="npc-text-item-icon" src={abilityIconPath(ability.id)} alt="" width={20} height={20} />}
          New ability: {ability.name}
        </span>
      )
    )}
  </p>;
};

/**
 * The quest just turned in at this NPC (plan 13 §10): its completion line at
 * the top of the refreshed quest page, or in place of the greeting when the
 * NPC has nothing left to offer, with what the turn-in gave beneath it.
 */
export const NpcCompletionNotice: React.FC<{ completion: QuestCompletionNotice }> = ({ completion }) => {
  const quest = getQuest(completion.questId);
  const rewardAbilityId = questCardView(completion.questId, undefined, completion.playerClass)?.rewardAbility?.id;
  return <section className="npc-completion" role="status" aria-label={`${quest?.name ?? 'Quest'} complete`}
    data-testid="npc-completion" data-quest-id={completion.questId}>
    <p className="npc-completion-kicker">Quest complete{quest ? ` · ${quest.name}` : ''}</p>
    <blockquote className="npc-dialogue-greeting npc-completion-line"><NpcRichText text={completion.message} rewardAbilityId={rewardAbilityId} /></blockquote>
    <ReceivedLine questId={completion.questId} playerClass={completion.playerClass} alreadyKnown={completion.alreadyKnown} />
  </section>;
};
